import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Send, X, MessageCircle, ChevronDown } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_BG = "https://images.pexels.com/photos/2387873/pexels-photo-2387873.jpeg?auto=compress&cs=tinysrgb&w=1920";

// Parse [OPTIONS: A | B | C] tags from ORZA responses (Refs 15, 16)
function parseOrzaResponse(rawText) {
  const match = rawText.match(/\[OPTIONS:\s*([^\]]+)\]/i);
  if (match) {
    const cleanText = rawText.replace(/\[OPTIONS:[^\]]+\]/i, "").trim();
    const options = match[1].split("|").map(s => s.trim()).filter(Boolean);
    return { text: cleanText, options };
  }
  return { text: rawText, options: [] };
}

// Unified invite message — used everywhere across the platform
const INVITE_MESSAGE = `Hey, just figured out something crazy about myself.

Now I need your result too. 😄

Apparently we can compare our travel personalities and see how compatible we actually are.

Take the quiz:

https://travelorza.com/quiz

Send me your Soulprint when you're done. I'm genuinely curious what you get.`;

// Subject label lookup — replaces nested ternary chains (Ref nested ternaries)
const SUBJECT_LABELS = {
  partner: "Invite",
  friend: "Invite",
  family: "Invite",
  colleague: "Invite",
};
const SCRIPT_STEPS = {
  OPENING: "Hi, I'm Orza. Quick question — is there someone in your life you think about first? A partner, a close friend, family?",
};

// Guided quick replies — first 10 exchanges require no typing (Refinement 2)
const GUIDED_QUICK_REPLIES = [
  ["Partner / couple", "A close friend", "Family member", "Not sure yet"],
  ["Very different from me", "Similar in many ways", "Hard to say"],
  ["They seek comfort and beauty", "They chase adventure", "Slow and cultural", "Energy and people"],
  ["We click effortlessly", "It takes planning", "Haven't really tried"],
  ["I wonder what they'd get", "Probably very different from me", "I'd love to find out"],
  ["It resonates a lot", "Parts feel off", "Tell me more"],
  ["Adventure and discovery", "Depth and meaning", "Rest and beauty", "Food and culture", "Music and people"],
  ["A few times a year", "Once or twice a year", "Rarely", "I travel often"],
  ["Always with someone", "Mix of both", "Usually solo"],
  ["Yes, I'm curious", "Maybe", "What is it exactly?"],
];

// Continuation replies — always shown after guided phase exhausts; Invite always available for Soulprint users
const CONTINUATION_REPLIES = [
  "Tell me more",
  "That's interesting",
  "I want to understand this better",
  "Invite",
];

function getSessionId() {
  let id = localStorage.getItem("travelorza_session");
  if (!id) {
    id = `session_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("travelorza_session", id);
  }
  return id;
}

export default function Hero({ onPlanJourney }) {
  const [chatOpen, setChatOpen] = useState(false);
  const [isModalMode, setIsModalMode] = useState(false);
  const [messages, setMessages] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [guidedPhase, setGuidedPhase] = useState(0);
  const [contextSubject, setContextSubject] = useState(""); // tracks conversation subject for contextual share actions
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(getSessionId);
  const [scriptPhase, setScriptPhase] = useState(0);
  const [showScriptCTA, setShowScriptCTA] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const storedArchetype = localStorage.getItem("travelorza_archetype");
  const storedSecondary = localStorage.getItem("travelorza_secondary_archetype");
  const storedName = localStorage.getItem("travelorza_name");
  const storedDNA = localStorage.getItem("travelorza_dna_summary");

  // Access rule: ORZA is a Soulprint Companion — requires completed Soulprint
  const hasSoulprint = !!storedArchetype;

  // Personalized opener for returning visitors — identity-first, ends with reflection not travel
  function getOpeningMessage() {
    if (storedArchetype) {
      const firstName = storedName ? storedName.split(" ")[0] : "";
      const secondary = storedSecondary ? ` with a ${storedSecondary} undercurrent` : "";
      return `${firstName ? `Hey ${firstName}. ` : "Hey. "}Your Soulprint currently shows a strong ${storedArchetype} profile${secondary}. Has anything shifted for you since we last explored this?`;
    }
    return SCRIPT_STEPS.OPENING;
  }

  // Quick replies for the opening message to reduce friction
  function getOpeningQuickReplies() {
    if (storedArchetype) {
      return ["Something's shifted", "Tell me about my profile", "Invite"];
    }
    return []; // No guided replies for users without Soulprint — they see the gate
  }

  // Unified share action — always just "Invite" with the shared invite message
  function getShareActions() {
    return [{ label: "Invite", href: `https://wa.me/?text=${encodeURIComponent(INVITE_MESSAGE)}` }];
  }

  // Listen for enquiry submission — open Orza with context
  // State setters (setChatOpen, setIsModalMode, etc.) and browser globals are stable — empty deps intentional
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handler = (e) => {
      const { preferred_experience, archetype, name } = e.detail || {};
      const contextMsg = archetype
        ? `Welcome${name ? `, ${name}` : ""}! Based on your ${archetype} Soulprint, I can suggest some additional experiences that might align with what you're looking for. Would you like to explore similar journeys or alternatives?`
        : `Thank you for reaching out! Your Experience Architect will be in touch shortly. In the meantime, can I help you explore more experiences or answer any questions?`;
      setChatOpen(true);
      setIsModalMode(false);
      setMessages(prev => [...prev, { role: "assistant", content: contextMsg }]);
      setScriptPhase(3);
    };
    window.addEventListener("enquiry_submitted", handler);
    return () => window.removeEventListener("enquiry_submitted", handler);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading, showScriptCTA]);

  const closeModal = () => {
    setChatOpen(false);
    setIsModalMode(false);
    document.body.style.overflow = "";
  };

  // Unified close — always unlocks body scroll
  const closeOrza = () => {
    setChatOpen(false);
    setIsModalMode(false);
    document.body.style.overflow = "";
  };

  const openPanel = () => {
    setChatOpen(true);
    setIsModalMode(false);
    // Lock body scroll on mobile so the page cannot scroll behind ORZA
    if (window.innerWidth < 768) {
      document.body.style.overflow = "hidden";
    }
    if (messages.length === 0) {
      setMessages([{ role: "assistant", content: getOpeningMessage() }]);
      setQuickReplies(getOpeningQuickReplies());
      setScriptPhase(storedArchetype ? 3 : 1);
    }
  };

  const runScriptBridges = async (bridges) => {
    for (const content of bridges) {
      setIsLoading(true);
      await new Promise(r => setTimeout(r, 700));
      setIsLoading(false);
      setMessages(prev => [...prev, { role: "assistant", content }]);
      await new Promise(r => setTimeout(r, 300));
    }
  };

  const sendMessage = async (overrideMsg) => {
    const userMsg = (overrideMsg || input).trim();
    if (!userMsg || isLoading) return;
    setInput("");
    setQuickReplies([]);
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);

    if (scriptPhase === 1) setScriptPhase(2);
    if (scriptPhase === 2) {
      if (!storedArchetype) setShowScriptCTA(true);
      setScriptPhase(3);
    }

    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/concierge/chat`, {
        session_id: sessionId,
        message: userMsg,
        archetype: storedArchetype || undefined,
        secondary_archetype: storedSecondary || undefined,
        traveler_name: storedName || undefined,
        dna_summary: storedDNA || undefined,
      });

      // Parse [OPTIONS: ...] from ORZA response (Ref 16)
      const parsed = parseOrzaResponse(res.data.response);
      setMessages(prev => [...prev, { role: "assistant", content: parsed.text }]);

      // ORZA-provided options take priority; fall back to guided phase, then always continuation chips
      const nextPhase = guidedPhase + 1;
      setGuidedPhase(nextPhase);
      if (parsed.options.length > 0) {
        setQuickReplies(parsed.options);
      } else if (nextPhase < GUIDED_QUICK_REPLIES.length) {
        setQuickReplies(GUIDED_QUICK_REPLIES[nextPhase]);
      } else {
        setQuickReplies(CONTINUATION_REPLIES);
      }
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "There was a connection issue — send your message again and I'll respond." }]);
      setQuickReplies(CONTINUATION_REPLIES);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  // Contextual CTA label and action (Ref 19)
  function getCtaLabel() {
    return "Invite";
  }

  // ── CHAT CONTENT (shared between modal and panel) ──────────────────────────
  const shareActions = getShareActions();
  const chatContent = (
    <>
      {/* Header — more prominent, Soulprint-branded */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#C9A84C]/12 border border-[#C9A84C]/35 flex items-center justify-center">
            <MessageCircle size={14} className="text-[#C9A84C]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#FAFAFA] tracking-wide">Orza</p>
            <p className="text-[9px] text-[#C9A84C]/60 tracking-[0.2em] uppercase">Soulprint Companion</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Persistent contextual share action (Refinements 3 & 4) */}
          {shareActions.map(a => (
            <a key={a.label} href={a.href} target="_blank" rel="noopener noreferrer"
              className="text-[9px] px-2.5 py-1 rounded-full border border-[#C9A84C]/25 text-[#C9A84C]/70 hover:bg-[#C9A84C]/10 hover:text-[#C9A84C] transition-all whitespace-nowrap hidden sm:block">
              {a.label}
            </a>
          ))}
          <button onClick={closeOrza} className="text-[#71717A] hover:text-white p-1">
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Messages — or Soulprint gate if no quiz completed */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {!hasSoulprint ? (
          /* ── SOULPRINT GATE: no free-text conversation before quiz ─────── */
          <div className="h-full flex flex-col items-center justify-center px-7 text-center">
            <div className="w-10 h-px bg-[#C9A84C]/30 mx-auto mb-7" />
            <h3 className="font-heading text-xl md:text-2xl font-light text-white mb-4 leading-snug">
              Before we continue, let&apos;s discover your Soulprint.
            </h3>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-8 max-w-xs">
              ORZA works best when it understands the archetype currently guiding your journey.
            </p>
            <Link
              to="/quiz"
              onClick={closeOrza}
              data-testid="orza-discover-soulprint-btn"
              className="btn-gold px-8 py-3 text-xs tracking-[0.22em] uppercase"
            >
              Discover My Soulprint
            </Link>
          </div>
        ) : (
          /* ── NORMAL CHAT (Soulprint users only) ─────────────────────────── */
          <div className="px-5 py-4 space-y-3">
            {messages.map((msg, i) => (
              <div key={`${msg.role}-${i}`} className={`max-w-[88%] p-3.5 rounded-xl ${msg.role === "user" ? "ml-auto bg-white/5 border border-white/8" : "chat-message-ai pl-4"}`}>
                {msg.role === "assistant" && <p className="text-[9px] uppercase tracking-[0.2em] text-[#C9A84C] mb-1.5">Orza</p>}
                <p className={`text-sm leading-relaxed ${msg.role === "user" ? "text-[#FAFAFA]" : "text-[#D4D4D8]"}`}>{msg.content}</p>
              </div>
            ))}
            {isLoading && (
              <div className="chat-message-ai max-w-[88%] p-3.5 rounded-xl pl-4">
                <p className="text-[9px] uppercase tracking-[0.2em] text-[#C9A84C] mb-1.5">Orza</p>
                <div className="flex gap-1.5 items-center h-4">{[0,1,2].map(dot => <span key={dot} className="typing-dot w-1.5 h-1.5 rounded-full bg-[#C9A84C]/60 inline-block" />)}</div>
              </div>
            )}
            {/* Quick-reply chips — "Invite" opens WhatsApp, all others send to ORZA */}
            {quickReplies.length > 0 && !isLoading && (
              <div className="flex flex-wrap gap-2 px-1 pt-1">
                {quickReplies.map(r => r === "Invite" ? (
                  <a key="Invite" href={`https://wa.me/?text=${encodeURIComponent(INVITE_MESSAGE)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-xs px-3 py-1.5 border border-[#C9A84C]/30 text-[#C9A84C]/80 rounded-full hover:bg-[#C9A84C]/10 hover:text-[#C9A84C] transition-all duration-150">
                    Invite
                  </a>
                ) : (
                  <button key={r} onClick={() => sendMessage(r)}
                    className="text-xs px-3 py-1.5 border border-[#C9A84C]/30 text-[#C9A84C]/80 rounded-full hover:bg-[#C9A84C]/10 hover:text-[#C9A84C] transition-all duration-150">
                    {r}
                  </button>
                ))}
              </div>
            )}
            {/* Script CTA → Invite */}
            {showScriptCTA && !isLoading && (
              <div className="pt-2">
                <a href={`https://wa.me/?text=${encodeURIComponent(INVITE_MESSAGE)}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="orza-script-cta-btn"
                  className="block w-full btn-gold text-xs py-3 text-center">
                  Invite
                </a>
                <button onClick={() => { setShowScriptCTA(false); setIsModalMode(false); }} className="w-full text-center text-[10px] text-[#71717A] hover:text-white mt-2 py-1 transition-colors">
                  Continue conversation
                </button>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input — only for Soulprint users */}
      {hasSoulprint && (
      <div className="px-5 py-4 border-t border-white/5 shrink-0">
        <div className="flex gap-2 items-end">
          <input
            ref={inputRef}
            data-testid="chat-input"
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Or type anything..."
            className="flex-1 bg-transparent border-b border-white/20 focus:border-[#C9A84C] outline-none text-sm text-white placeholder:text-white/25 pb-2 transition-colors"
          />
          <button
            data-testid="chat-send-btn"
            onClick={() => sendMessage()}
            disabled={isLoading || !input.trim()}
            className="w-8 h-8 flex items-center justify-center bg-[#C9A84C] disabled:bg-[#C9A84C]/25 text-black disabled:text-black/30 rounded-sm"
          >
            <Send size={13} />
          </button>
        </div>
        {/* Mobile share action (always visible) */}
        <div className="mt-3 sm:hidden">
          {shareActions.map(a => (
            <a key={a.label} href={a.href} target="_blank" rel="noopener noreferrer"
              className="block text-center text-[9px] tracking-[0.2em] uppercase text-[#C9A84C]/60 hover:text-[#C9A84C] transition-colors py-1">
              {a.label} →
            </a>
          ))}
        </div>
      </div>
      )}{/* end hasSoulprint input */}
    </>
  );

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex flex-col">
      {/* Background — darker overlay for readability */}
      <div className="absolute inset-0 z-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover" onError={e => { e.target.src = "https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1920&q=80"; }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/65 to-black/95" />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          MOBILE HERO — Cohesive single-block layout (md:hidden)
          ═══════════════════════════════════════════════════════════════════ */}
      <div className="md:hidden relative z-10 flex flex-col items-center justify-center text-center px-5" style={{ minHeight: "100dvh", paddingTop: 80, paddingBottom: 40 }}>

        <div className="flex flex-col items-center w-full animate-fade-in-up" style={{ animationFillMode: "forwards" }}>

          {/* Brand */}
          <img
            src="https://customer-assets.emergentagent.com/job_soulprint-travel/artifacts/45txajc6_Full%20logo%20-%20wbg.svg"
            alt="Travelorza"
            style={{ height: 64, width: "auto", display: "block", margin: "0 auto 10px" }}
            onError={e => { e.target.style.display = "none"; }}
          />
          <p className="overline text-[9px] tracking-[0.2em] mb-6">Soulprint-Led Experience Architecture</p>

          {/* Headline block */}
          <div className="mb-4">
            <h1>
              <span
                className="block font-heading font-light text-white tracking-tight leading-none whitespace-nowrap"
                style={{ fontSize: "clamp(1.45rem, 7.5vw, 2.1rem)" }}
              >
                BEGIN WITH WHO YOU ARE
              </span>
              <span className="block text-[10px] font-bold uppercase tracking-[0.55em] text-white/65 mt-2">
                NOT WHERE YOU GO
              </span>
            </h1>
          </div>

          {/* Quote */}
          <p className="font-heading text-sm font-light italic text-[#C9A84C] tracking-wide mb-8 max-w-xs mx-auto leading-relaxed">
            &ldquo;Because the right journey isn&apos;t chosen. It&apos;s aligned.&rdquo;
          </p>

          {/* CTA */}
          <Link
            to="/quiz"
            data-testid="hero-soulprint-quiz-btn"
            className="btn-gold w-full text-sm tracking-[0.22em] uppercase mb-3 text-center"
            style={{ maxWidth: 300, paddingTop: 16, paddingBottom: 16 }}
          >
            Discover Your Soulprint
          </Link>
          <p className="text-[10px] text-[#71717A] tracking-[0.22em] uppercase">
            A 3-minute discovery that changes how you travel.
          </p>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          DESKTOP HERO — Original centered layout (hidden below md)
          ═══════════════════════════════════════════════════════════════════ */}
      <div className="hidden md:flex relative z-10 flex-1 flex-col items-center justify-center text-center px-6 max-w-4xl mx-auto w-full" style={{ paddingTop: 80, paddingBottom: 24 }}>

        {/* Logo */}
        <div className="opacity-0 animate-fade-in-up" style={{ animationFillMode: "forwards", marginBottom: 10 }}>
          <img
            src="https://customer-assets.emergentagent.com/job_soulprint-travel/artifacts/45txajc6_Full%20logo%20-%20wbg.svg"
            alt="Travelorza"
            style={{ height: "clamp(80px, 18vw, 240px)", width: "auto", display: "block", margin: "0 auto", maxHeight: "none", maxWidth: "none" }}
            onError={e => { e.target.style.display = "none"; }}
          />
        </div>

        {/* Overline */}
        <div className="opacity-0 animate-fade-in-up delay-100" style={{ animationFillMode: "forwards", marginBottom: 14 }}>
          <p className="overline">Soulprint-Led Experience Architecture</p>
        </div>

        {/* 3-line headline — WE removed, BEGIN/NOT WHERE unified */}
        <div className="opacity-0 animate-fade-in-up delay-200" style={{ animationFillMode: "forwards", marginBottom: 14 }}>
          <h1>
            <span
              className="block font-heading font-light text-[#FAFAFA] tracking-tight leading-tight whitespace-nowrap overflow-hidden"
              style={{ fontSize: "clamp(1.2rem, 5vw, 5.5rem)" }}
            >
              BEGIN WITH WHO YOU ARE
            </span>
            <span className="block text-[10px] md:text-xs font-semibold uppercase tracking-[0.45em] text-[#FAFAFA]/60 mt-1">NOT WHERE YOU GO</span>
          </h1>
        </div>

        {/* Supporting */}
        <div className="opacity-0 animate-fade-in-up delay-300" style={{ animationFillMode: "forwards", marginBottom: 22 }}>
          <p className="font-heading text-sm md:text-lg font-light italic text-[#C9A84C] tracking-wide">
            &ldquo;Because the right journey isn&apos;t chosen. It&apos;s aligned.&rdquo;
          </p>
        </div>

        {/* CTA + microcopy */}
        <div className="opacity-0 animate-fade-in-up" style={{ animationDelay: "0.4s", animationFillMode: "forwards" }}>
          <Link to="/quiz" data-testid="hero-soulprint-quiz-btn-desktop" className="btn-gold text-xs px-12 py-3.5 inline-block mb-2">
            Discover Your Soulprint
          </Link>
          <p className="text-[10px] text-[#71717A] tracking-widest uppercase">
            A 3-minute discovery that changes how you travel.
          </p>
        </div>

        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 opacity-0 animate-fade-in delay-500" style={{ animationFillMode: "forwards" }}>
          <ChevronDown className="text-[#C9A84C] animate-bounce" size={18} />
        </div>
      </div>

      {/* ── ORZA CENTERED MODAL (first visit) ──────────────────────────────────── */}
      {chatOpen && isModalMode && (
        <div
          data-testid="concierge-chat-panel"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6 animate-fade-in"
          style={{ background: "rgba(0,0,0,0.65)", animationFillMode: "forwards" }}
        >
          <div
            className="glass rounded-2xl border border-[#C9A84C]/15 shadow-2xl flex flex-col animate-fade-in-up w-full"
            style={{ width: "min(480px, calc(100vw - 32px))", height: "min(560px, 85vh)", animationFillMode: "forwards" }}
          >
            {chatContent}
          </div>
        </div>
      )}

      {/* ── ORZA PANEL ─────────────────────────────────────────────────────────
          Mobile: fixed full-screen overlay (100dvh × 100vw, opaque, scroll-locked)
          Desktop: fixed side panel bottom-right (unchanged)
          ─────────────────────────────────────────────────────────────────────── */}
      {chatOpen && !isModalMode && (
        <div
          data-testid="concierge-chat-panel"
          className="fixed z-50 slide-up
                     inset-0
                     md:inset-auto md:bottom-10 md:right-8 md:w-full md:max-w-md"
          style={{ touchAction: "none" }}
        >
          {/* Mobile: opaque full-screen. Desktop: glass side panel */}
          <div
            className="flex flex-col h-full
                       bg-[#050505]
                       md:bg-transparent md:glass md:rounded-2xl md:border md:border-white/10 md:shadow-2xl md:h-[520px]"
          >
            {chatContent}
          </div>
        </div>
      )}

      {/* ── FAB — bottom-right, clear of Emergent badge on all screen sizes ── */}
      {!chatOpen && (
        <button
          data-testid="chat-fab-btn"
          onClick={openPanel}
          className="fixed bottom-20 right-4 md:bottom-10 md:right-8 z-50 w-12 h-12 md:w-14 md:h-14 bg-[#C9A84C] text-black rounded-full flex items-center justify-center shadow-lg hover:bg-[#D4B86A] transition-colors"
        >
          <MessageCircle size={20} />
        </button>
      )}
    </section>
  );
}
